import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { menuData } from '../data/mockData';
import { ScrollArea } from './ui/scroll-area';

export const MenuModal = ({ isOpen, onClose, initialCategory }) => {
  const [activeTab, setActiveTab] = useState(initialCategory);

  React.useEffect(() => {
    setActiveTab(initialCategory);
  }, [initialCategory]);

  const tabs = [
    { id: 'pizzas', label: 'Pizzas' },
    { id: 'sandwiches', label: 'Sandwichs' },
    { id: 'tacos', label: 'Tacos' },
    { id: 'burgers', label: 'Burgers' },
    { id: 'assiettes', label: 'Assiettes' },
    { id: 'braises', label: 'Rôtis' },
    { id: 'salades', label: 'Salades' },
    { id: 'paninis', label: 'Paninis' },
    { id: 'texmex', label: 'Tex Mex' },
    { id: 'boissons', label: 'Boissons' },
    { id: 'desserts', label: 'Desserts' },
    { id: 'glaces', label: 'Glaces' },
    { id: 'menus', label: 'Menus' }
  ];

  const renderPizzas = () => (
    <div className="space-y-8">
      {menuData.pizzas.offers && (
        <div className="space-y-4">
          <h3 className="font-bold text-[#D4A85F] text-2xl text-center mb-4">{menuData.pizzas.offers.title}</h3>
          
          <div className="grid md:grid-cols-2 gap-4">
            {menuData.pizzas.offers.sections.map((section, idx) => (
              <div key={idx} className="bg-gradient-to-br from-[#ECEC75] to-[#D4A85F] p-5 rounded-lg shadow-lg">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-2xl">{section.emoji}</span>
                  <h4 className="font-bold text-black text-lg">{section.type}</h4>
                </div>
                <p className="text-black font-bold text-xl mb-3">{section.offer}</p>
                <div className="space-y-1">
                  {section.conditions.map((condition, condIdx) => (
                    <p key={condIdx} className="text-gray-900 text-sm">👉 {condition}</p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {menuData.pizzas.promos && (
        <div className="space-y-3">
          <h3 className="text-[#D4A85F] font-bold text-xl">Promos Pizzas</h3>
          <div className="grid gap-3">
            {menuData.pizzas.promos.map((promo, idx) => (
              <div key={idx} className="bg-gradient-to-br from-[#D4A85F] to-[#E2B978] p-4 rounded-lg shadow-lg">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h4 className="text-black font-bold text-lg">{promo.name}</h4>
                    <p className="text-gray-900 font-medium text-sm">{promo.description}</p>
                  </div>
                  <span className="text-black font-bold text-xl ml-3">{promo.price}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-[#2A2A2A] p-4 rounded-lg mb-4">
        <p className="text-white text-center font-semibold">Tailles: Junior | Senior | Mega</p>
      </div>

      {menuData.pizzas.categories.map((category, catIdx) => (
        <div key={catIdx} className="space-y-4">
          <h3 className="text-[#D4A85F] font-bold text-xl mb-4">{category.subtitle}</h3>
          <div className="grid gap-4">
            {category.items.map((item, idx) => (
              <div
                key={idx}
                className={`bg-[#2A2A2A] p-4 rounded-lg border-2 ${
                  item.featured ? 'border-[#ECEC75]' : 'border-transparent'
                } hover:border-[#D4A85F] transition-all duration-200`}
              >
                <div className="flex justify-between items-start mb-2">
                  <h4 className="text-white font-semibold text-lg flex-1">
                    {item.name}
                    {item.featured && (
                      <span className="ml-2 text-xs bg-[#ECEC75] text-black px-2 py-1 rounded">SPÉCIALITÉ</span>
                    )}
                  </h4>
                </div>
                <p className="text-gray-400 text-sm mb-3">{item.description}</p>
                <div className="flex gap-3 text-sm">
                  {item.prices && (
                    <>
                      <span className="text-[#D4A85F] font-semibold">Junior: {item.prices.junior}</span>
                      <span className="text-[#D4A85F] font-semibold">Senior: {item.prices.senior}</span>
                      <span className="text-[#D4A85F] font-semibold">Mega: {item.prices.mega}</span>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {menuData.pizzas.supplement && (
        <div className="bg-[#D4A85F] p-3 rounded-lg mt-4">
          <p className="text-black font-semibold text-center">{menuData.pizzas.supplement}</p>
        </div>
      )}
    </div>
  );

  const renderSandwiches = () => (
    <div className="space-y-4">
      {menuData.sandwiches.subtitle && (
        <div className="bg-[#D4A85F] p-3 rounded-lg">
          <p className="text-black font-semibold text-center">{menuData.sandwiches.subtitle}</p>
        </div>
      )}
      <div className="grid gap-4">
        {menuData.sandwiches.items.map((item, idx) => (
          <div key={idx} className="bg-[#2A2A2A] p-4 rounded-lg hover:border-2 hover:border-[#D4A85F] transition-all duration-200">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <h4 className="text-white font-semibold text-lg">{item.name}</h4>
                <p className="text-gray-400 text-sm mt-1">{item.description}</p>
              </div>
              <span className="text-[#D4A85F] font-bold text-lg ml-4">{item.price}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderTacos = () => (
    <div className="space-y-4">
      <div className="grid gap-4">
        {menuData.tacos.items.map((item, idx) => (
          <div key={idx} className="bg-[#2A2A2A] p-4 rounded-lg hover:border-2 hover:border-[#D4A85F] transition-all duration-200">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <h4 className="text-white font-semibold text-lg">{item.name}</h4>
                <p className="text-gray-400 text-sm mt-1">{item.description}</p>
              </div>
              <span className="text-[#D4A85F] font-bold text-lg ml-4">{item.price}</span>
            </div>
          </div>
        ))}
      </div>
      {menuData.tacos.viandes && (
        <div className="bg-[#D4A85F] p-4 rounded-lg mt-4">
          <p className="text-black font-semibold">{menuData.tacos.viandes}</p>
        </div>
      )}
    </div>
  );

  const renderBurgers = () => (
    <div className="space-y-4">
      <div className="grid gap-4">
        {menuData.burgers.items.map((item, idx) => (
          <div key={idx} className="bg-[#2A2A2A] p-4 rounded-lg hover:border-2 hover:border-[#D4A85F] transition-all duration-200">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <h4 className="text-white font-semibold text-lg">{item.name}</h4>
                <p className="text-gray-400 text-sm mt-1">{item.description}</p>
              </div>
              <span className="text-[#D4A85F] font-bold text-lg ml-4">{item.price}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderAssiettes = () => (
    <div className="space-y-4">
      {menuData.assiettes.subtitle && (
        <div className="bg-[#D4A85F] p-3 rounded-lg">
          <p className="text-black font-semibold text-center">{menuData.assiettes.subtitle}</p>
        </div>
      )}
      <div className="grid gap-4">
        {menuData.assiettes.items.map((item, idx) => (
          <div key={idx} className="bg-[#2A2A2A] p-4 rounded-lg hover:border-2 hover:border-[#D4A85F] transition-all duration-200">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <h4 className="text-white font-semibold text-lg">{item.name}</h4>
                <p className="text-gray-400 text-sm mt-1">{item.description}</p>
              </div>
              <span className="text-[#D4A85F] font-bold text-lg ml-4">{item.price}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderBraises = () => (
    <div className="space-y-4">
      <div className="grid gap-4">
        {menuData.braises.items.map((item, idx) => (
          <div key={idx} className="bg-[#2A2A2A] p-4 rounded-lg hover:border-2 hover:border-[#D4A85F] transition-all duration-200">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <h4 className="text-white font-semibold text-lg">{item.name}</h4>
                <p className="text-gray-400 text-sm mt-1">{item.description}</p>
              </div>
              <span className="text-[#D4A85F] font-bold text-lg ml-4">{item.price}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderSalades = () => (
    <div className="space-y-4">
      <div className="bg-[#D4A85F] p-3 rounded-lg">
        <p className="text-black font-semibold text-center">Toutes les salades: {menuData.salades.price}</p>
      </div>
      <div className="grid gap-4">
        {menuData.salades.items.map((item, idx) => (
          <div key={idx} className="bg-[#2A2A2A] p-4 rounded-lg hover:border-2 hover:border-[#D4A85F] transition-all duration-200">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <h4 className="text-white font-semibold text-lg">{item.name}</h4>
                <p className="text-gray-400 text-sm mt-1">{item.description}</p>
              </div>
              <span className="text-[#D4A85F] font-bold text-lg ml-4">{item.price}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderPaninis = () => (
    <div className="space-y-4">
      <div className="bg-[#D4A85F] p-3 rounded-lg">
        <p className="text-black font-semibold text-center">
          Tous les paninis: {menuData.paninis.price}
          {menuData.paninis.note && <span className="block text-sm mt-1">{menuData.paninis.note}</span>}
        </p>
      </div>
      <div className="grid gap-4">
        {menuData.paninis.items.map((item, idx) => (
          <div key={idx} className="bg-[#2A2A2A] p-4 rounded-lg hover:border-2 hover:border-[#D4A85F] transition-all duration-200">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <h4 className="text-white font-semibold text-lg">{item.name}</h4>
                <p className="text-gray-400 text-sm mt-1">{item.description}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderTexMex = () => (
    <div className="space-y-6">
      {menuData.texmex.categories.map((category, catIdx) => (
        <div key={catIdx} className="space-y-3">
          <h3 className="text-[#D4A85F] font-bold text-xl">{category.name}</h3>
          <div className="grid gap-3">
            {category.items.map((item, idx) => (
              <div key={idx} className="bg-[#2A2A2A] p-3 rounded-lg flex justify-between items-center">
                <span className="text-white font-medium">{item.quantity}</span>
                <span className="text-[#D4A85F] font-bold">{item.price}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  const renderBoissons = () => (
    <div className="space-y-4">
      <div className="grid gap-4">
        {menuData.boissons.items.map((item, idx) => (
          <div key={idx} className="bg-[#2A2A2A] p-4 rounded-lg hover:border-2 hover:border-[#D4A85F] transition-all duration-200">
            <div className="flex justify-between items-center">
              <h4 className="text-white font-semibold text-lg">{item.name}</h4>
              <span className="text-[#D4A85F] font-bold text-lg">{item.price}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderDesserts = () => (
    <div className="space-y-4">
      <div className="grid gap-4">
        {menuData.desserts.items.map((item, idx) => (
          <div key={idx} className="bg-[#2A2A2A] p-4 rounded-lg hover:border-2 hover:border-[#D4A85F] transition-all duration-200">
            <div className="flex justify-between items-center">
              <h4 className="text-white font-semibold text-lg">{item.name}</h4>
              <span className="text-[#D4A85F] font-bold text-lg">{item.price}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderGlaces = () => (
    <div className="space-y-4">
      {menuData.glaces.subtitle && (
        <div className="bg-[#D4A85F] p-3 rounded-lg">
          <p className="text-black font-semibold text-center text-lg">{menuData.glaces.subtitle}</p>
        </div>
      )}
      <div className="grid gap-4">
        {menuData.glaces.items.map((item, idx) => (
          <div key={idx} className="bg-[#2A2A2A] p-4 rounded-lg hover:border-2 hover:border-[#D4A85F] transition-all duration-200">
            <div className="flex justify-between items-center">
              <h4 className="text-white font-semibold text-lg">{item.name}</h4>
              <span className="text-[#D4A85F] font-bold text-lg">{item.price}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderMenus = () => (
    <div className="space-y-4">
      <div className="grid gap-4">
        {menuData.menus.items.map((item, idx) => (
          <div key={idx} className="bg-gradient-to-br from-[#D4A85F] to-[#E2B978] p-5 rounded-lg shadow-lg">
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-black font-bold text-xl">{item.name}</h4>
              <span className="text-black font-bold text-2xl">{item.price}</span>
            </div>
            <p className="text-gray-900 font-medium">{item.description}</p>
          </div>
        ))}
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'pizzas':
        return renderPizzas();
      case 'sandwiches':
        return renderSandwiches();
      case 'tacos':
        return renderTacos();
      case 'burgers':
        return renderBurgers();
      case 'assiettes':
        return renderAssiettes();
      case 'braises':
        return renderBraises();
      case 'salades':
        return renderSalades();
      case 'paninis':
        return renderPaninis();
      case 'texmex':
        return renderTexMex();
      case 'boissons':
        return renderBoissons();
      case 'desserts':
        return renderDesserts();
      case 'glaces':
        return renderGlaces();
      case 'menus':
        return renderMenus();
      default:
        return null;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#1A1A1A] border-[#D4A85F] max-w-5xl max-h-[85vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="text-[#D4A85F] text-3xl font-bold flex items-center justify-between">
            <span>Menu Le Tassili</span>
            <button onClick={onClose} className="text-white hover:text-[#D4A85F] transition-colors">
              <X size={28} />
            </button>
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-[#2A2A2A] w-full flex-wrap h-auto gap-2 p-2">
            {tabs.map((tab) => (
              <TabsTrigger
                key={tab.id}
                value={tab.id}
                className="data-[state=active]:bg-[#D4A85F] data-[state=active]:text-black text-white"
              >
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <ScrollArea className="h-[calc(85vh-180px)] mt-4">
            <div className="pr-4">{renderContent()}</div>
          </ScrollArea>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
